import axios, { AxiosError } from "axios";
import { auth } from "../firebase";

interface ApiError {
  message?: string;
}

const backendAPI = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

async function getAuthHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error("User not logged in");
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

export type CockpitMoodId =
  | "early"
  | "growing"
  | "stable"
  | "attention"
  | "momentum";

export type CockpitMetricProvenance =
  | "paid_orders"
  | "clients"
  | "products"
  | "none";

export type CockpitDestination = {
  type: string;
  section?: string;
  table?: string;
  filter?: string;
  todoTitle?: string;
};

export type CockpitMetric = {
  value: number;
  previousValue: number | null;
  changePercent: number | null;
  available: boolean;
  provenance: CockpitMetricProvenance;
  note: string | null;
  total: number | null;
  window: string | null;
};

export type CockpitInsight = {
  type: string;
  summary: string;
  severity: string;
  evidence: Record<string, unknown> | null;
  destination: CockpitDestination;
};

export type CockpitSeriesPoint = {
  period: string;
  value: number;
};

export type BusinessCockpitSnapshot = {
  version: number;
  userBusinessId: number;
  assembledAt: string;
  currency: string;
  phase: "preparing" | "acquiring" | "post_first_customer" | null;
  mood: {
    id: CockpitMoodId;
    label: string;
    explanation: string;
  };
  metrics: {
    revenue: CockpitMetric;
    orders: CockpitMetric;
    customers: CockpitMetric;
    products: CockpitMetric;
  };
  series: {
    revenue: CockpitSeriesPoint[] | null;
    grain: "month" | "week" | null;
  };
  inventory: {
    lowStockCount: number;
    threshold: number;
    products: Array<{
      id: number;
      title: string;
      quantity: number;
    }>;
  };
  availability: {
    paymentsConnected: boolean;
    hasPaidOrders: boolean;
    hasWebsite: boolean;
    marketingConnected: boolean;
    businessStateProjected: boolean;
    revenueTracked: boolean;
  };
  insights: {
    changing: CockpitInsight | null;
    attention: CockpitInsight | null;
    opportunity: CockpitInsight | null;
  };
  destinations: {
    modules: CockpitDestination;
    finance: CockpitDestination;
    payments: CockpitDestination;
    customers: CockpitDestination;
    products: CockpitDestination;
    lowInventory: CockpitDestination;
    orders: CockpitDestination;
    marketing: CockpitDestination;
    website: CockpitDestination;
  };
};

export const get_business_cockpit = async (
  userBusinessId: number
): Promise<BusinessCockpitSnapshot> => {
  try {
    const headers = await getAuthHeaders();
    const response = await backendAPI.get(
      `business-cockpit/business/${userBusinessId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      (error as AxiosError<ApiError>)?.response?.data?.message ||
        "Failed to load business cockpit"
    );
  }
};
